import { cn } from "@/libs/utils";
import {
  getProductDetailHref,
  isProductDetailActive,
} from "@/libs/product/detail-href";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v2-components/ui/accordion";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import CrossThinIcon from "@/v2-components/core/icon/cross-thin-icon";
import BHapticsLogo from "@/v2-components/core/logo/bhaptics-logo";
import {
  SheetClose,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import {
  SubMenu,
  getNavigationMenus,
  Menu,
  type BadgeType,
  type PanelKey,
} from "@/v2-components/global-navigation-bar/_const/navigation-menus";
import {
  BADGED_LINEUP_HREF,
  useBundleSetBadges,
} from "@/v2-components/global-navigation-bar/use-bundle-set-badges";
import { useNavigationMenu } from "@/v2-components/global-navigation-bar/navigation-menu";
import { useRouter } from "next/router";
import Link from "next/link";
import { createContext, useContext, useState } from "react";
import { Fragment } from "react";
import { ProductSlug } from "@/api/product-api";
import { Dot } from "@/v2-components/dot";
import { useTrans } from "@/utils/useTrans";
import { MenuItemBadge } from "@/v2-components/global-navigation-bar/menu-item-badge";
import { useProducts } from "@/services/product/useProductService";
import { useLocale } from "@/store/locale-store";
import GlobeIcon from "@/v2-components/core/icon/globe-icon";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import CountryFlag from "@/components/CountryFlag";
import { getSubNavTabs } from "@/v2-components/pages/games/GamesSubNav";
import { PopularTitlesRail } from "@/v2-components/global-navigation-bar/popular-titles-rail";
import Image from "next/image";
import {
  STORE_COPY,
  STORE_LINEUP_LINKS,
  storeRowBadge,
  splitStoreGroups,
} from "@/v2-components/global-navigation-bar/_const/store-panel";
import type { Product } from "@/api/product-api";
import { usePricedFilter } from "@/hooks/usePricedFilter";

interface MenuViewContextValue {
  onClose?: () => void;
}

interface RootProps {
  onClose?: () => void;
  children?: React.ReactNode;
}

const MenuViewContext = createContext<MenuViewContextValue | null>(null);

const useMenuViewContext = () => {
  const context = useContext(MenuViewContext);
  if (!context) {
    throw new Error(
      "MenuView compound components cannot be rendered outside the MenuView component",
    );
  }
  return context;
};

/**
 * 메뉴 항목 링크의 **활성 판정 + 클릭 처리**를 한 곳에 둔다 (#5386).
 *
 * ## 왜 훅인가 — 같은 결함이 이 파일에 5곳 있었다
 *
 * `<Link>` 4곳과 `WithoutContent` 1곳이 **같은 모양**으로 틀렸다:
 *
 * ```tsx
 * const current = router.pathname === item.href;   // ← 아래 두 문제의 뿌리
 * onClick={(e) => { if (current) { e.preventDefault(); onClose?.(); } }}
 * ```
 *
 * 1. **`pathname` 은 동적 라우트에서 패턴 문자열**이다(`/shop/buy-tactsuit/[slug]`).
 *    제품 href(`/shop/buy-tactsuit/tactsuit-air`)와 **절대 같아지지 않아** 선택 표시(볼드)가
 *    어떤 제품에서도 안 떴다.
 * 2. `onClose()` 가 **그 조건 안에만** 있었다. 조건이 항상 거짓이니 **이동해도 메뉴가 닫히지
 *    않았다** — 신고된 증상(#5386)이 둘 다 이 한 뿌리에서 나왔다.
 *
 * 한 곳만 고치면 나머지 4곳이 남으므로 훅으로 모은다.
 *
 * ⚠️ **이동할 때도 닫는다.** `current` 면 이동이 없으니 `preventDefault` 만 추가로 한다.
 *    `onClose()` 는 상태 업데이트라 `<Link>` 의 내부 네비게이션을 막지 않는다.
 */
function useMenuLink(href: string, isActiveOverride?: boolean) {
  const router = useRouter();
  const { onClose } = useMenuViewContext();
  // 정적 경로 비교도 `asPath` 로 한다 — `pathname` 은 위 이유로 쓰지 않는다.
  // `trailingSlash: true` 라 도착 경로는 슬래시로 끝나므로 양쪽을 정규화한다.
  const strip = (v: string) => v.split("?")[0].replace(/\/+$/, "");
  const current = isActiveOverride ?? strip(router.asPath) === strip(href);
  return {
    current,
    onClick: (e: { preventDefault: () => void }) => {
      if (current) e.preventDefault();
      onClose?.();
    },
  };
}

function Root({ onClose = () => {}, children }: RootProps) {
  return (
    <MenuViewContext.Provider value={{ onClose }}>
      {children}
    </MenuViewContext.Provider>
  );
}

interface Props {
  onLanguageButtonClick: () => void;
  onClose?: () => void;
}

export function MenuView({ onLanguageButtonClick, onClose }: Props) {
  const { languageCode } = useLocale();
  const navigationMenus = getNavigationMenus(languageCode);

  /* 현재 위치의 섹션을 펼친 채로 시작한다.
     ⚠️ 종전엔 `router.pathname.split("/")[1]`(예: `"tactsuit"`)을 초기값으로 썼는데
     `AccordionItem` 의 value 는 `"TactSuit"`·`"games"` 여서 **어떤 항목과도 맞지 않았다** —
     항상 전부 닫힌 채로 열렸다. 이제 양쪽 모두 패널 키를 쓴다. */
  /**
   * ★**활성 메뉴를 여기서 다시 계산하지 않는다 — 컨텍스트가 이미 갖고 있다.**
   *
   * 종전엔 `getActiveMenuName(navigationMenus, router.asPath)` 로 **따로** 계산해서,
   * 경로로 못 가르는 화면의 override 를 못 받았다. 게임 상세(`/games/experiences/{slug}`)
   * 는 PC·VR 공용이라 PC 게임에서도 **VR 패널이 펼쳐진 채로** 시트가 열렸다
   * (2026-09-02 실측 — 데스크탑 볼드와 같은 원인, 다른 표면).
   *
   * 같은 값을 두 곳에서 계산하면 한쪽만 고쳐진다. 이 시트는 `NavigationMenuProvider`
   * 안에 있으므로(`global-navigation-bar/index.tsx`) 그 값을 그대로 읽는다.
   */
  const { activeMenuName } = useNavigationMenu();
  const [value, setValue] = useState<string | undefined>(
    () => navigationMenus.find((m) => m.name === activeMenuName)?.panel,
  );

  return (
    <Root onClose={onClose}>
      <Fragment>
        {/* 시안 `Menu Sheets`(`29302:21467`) — h64 `px-16 py-20`, 닫기 24 @16,
            로고 100×24. ★`mt-0` 을 명시한다: `SheetHeader` 기본이 `-mt-4 laptop:-mt-8`
            로 시트 패딩을 상쇄하는 값인데, 시트를 `p-0` 으로 바꿨으므로 그대로 두면
            헤더가 위로 잘려 올라간다(패딩만 덮으면 겹치는 알려진 자리). */}
        <SheetHeader className="mt-0 items-center px-4 py-5 laptop:mt-0 laptop:py-5">
          {/* ★**탭 영역을 44 로 넓힌다.** 시트가 시안대로 화면 전체를 덮으면서 스크림이
              시트에 완전히 가려져, <640px 에서는 **바깥을 눌러 닫는 경로가 사라졌다**
              (≥640px 은 `sm:max-w-sm`=384 라 스크림이 남는다). 모바일에서 이 X 가 유일한
              닫기 수단인데 아이콘 크기 그대로면 24×24 로, iOS 44 · Android 48 최소치에
              못 미친다. 여백으로만 키우고 `-m-2.5` 로 되돌려 **아이콘 위치는 시안(16,20)
              그대로** 둔다. Esc 는 동작하지만 폰엔 키가 없다. */}
          <SheetClose className="-m-2.5 p-2.5">
            <CrossThinIcon />
          </SheetClose>
          {/* 시안은 로고가 헤더 가운데에 있다. 닫기 버튼과 균형을 맞추려면 양옆이
              같아야 하므로, 로고를 늘어나는 칸 가운데에 둔다. */}
          <div className="flex flex-1 justify-center">
            <BHapticsLogo className="w-[100px]" />
          </div>
          {/* 닫기 아이콘(24)과 같은 폭을 오른쪽에 둬 로고가 실제 가운데에 오게 한다. */}
          <span aria-hidden className="size-6 shrink-0" />
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SheetDescription className="sr-only">Select a menu</SheetDescription>
        </SheetHeader>
        <nav className="min-h-0 flex-1 overflow-scroll pb-4">
          <Accordion
            type="single"
            collapsible
            value={value}
            onValueChange={setValue}
          >
            {navigationMenus.map((menu) => {
              /* ⚠️ 분기 키는 **메뉴 이름이 아니라 `menu.panel`** 이다. v1 은 여기와
                 데스크탑(`navigation-menu-content.tsx`)이 각자 문자열 리터럴로 메뉴를
                 식별해서, 이름을 바꾸면 한쪽만 고치고 다른 쪽은 조용히 안 그렸다. */
              const key = menu.panel ?? menu.name.toLowerCase();

              switch (menu.panel) {
                case "store":
                  return (
                    <TactSuitMenu
                      key={key}
                      menu={{ ...menu, panel: menu.panel }}
                    />
                  );
                case "pc":
                case "vr":
                  return (
                    <GamesMenu
                      key={key}
                      menu={{ ...menu, panel: menu.panel }}
                    />
                  );
                case "support":
                  return <WithContent key={key} menu={menu} />;
                default:
                  /* 패널 없음 = 아코디언 없이 바로 이동(Streamer / Developer). */
                  return <WithoutContent key={key} menu={menu} />;
              }
            })}
          </Accordion>
        </nav>
        <LocaleButton onClick={onLanguageButtonClick} />
      </Fragment>
    </Root>
  );
}

/**
 * 시안 1차 행(`29302:21472` 등) — **h56 균일** · `px-16` · 14/18.
 *
 * `size="lg"`(`py-[17px]`)만으로는 아이콘 유무에 따라 52/58 로 갈린다 — 아코디언 행에는
 * chevron(24)이 있고 `Streamer`/`Developer` 에는 없기 때문이다. 높이를 고정해 맞춘다.
 * ⚠️ `accordion.tsx` 의 `lg` 를 바꾸지 않는다 — 푸터 모바일 내비 · FAQ 등 **3곳**이 쓴다.
 */
const ROW = "h-14 py-0 font-400 data-[state=open]:font-600";

/**
 * 시안 패널 링크(`29304:21605` 등) — `pl-36 pr-16 py-10` · 14/18 tracking .25.
 * 좌인셋 36 은 1차 행(16)보다 깊어 **계층을 들여쓰기로** 나타낸다.
 *
 * 색은 두 단계다 — 첫 항목만 `coolgray-90` SemiBold, 나머지는 `coolgray-70` Regular.
 * 경로와 무관한 **위계 표현**이라 데스크톱 패널(`navigation-menu-content.tsx`)과 같은
 * 규칙으로 둔다.
 */
const PANEL_LINK =
  "block py-2.5 pl-9 pr-4 text-base leading-[18px] tracking-[0.25px] text-coolgray-70";
const PANEL_LINK_FIRST = "font-600 text-coolgray-90";

function WithContent({ menu }: { menu: Menu }) {
  const { t } = useTrans(["gnb"]);
  return (
    <AccordionItem
      value={menu.panel ?? menu.name.toLowerCase()}
      className="border-none"
    >
      <AccordionTrigger size="lg" className={ROW}>
        <span className="relative">
          {t(menu.name)}
          <Dot visible={menu.dotted} />
        </span>
        <ChevronDownIcon />
      </AccordionTrigger>
      <AccordionContent className="px-0 pb-2 pt-0">
        <ul className={cn("flex flex-col")}>
          {menu.subMenus.map((subMenu, index) => (
            <SubMenuItem key={subMenu.name} subMenu={subMenu} index={index} />
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}

function WithoutContent({ menu }: { menu: Menu }) {
  const { languageCode } = useLocale();
  const { t } = useTrans(["gnb"]);
  const router = useRouter();
  const link = useMenuLink(menu.href);
  return (
    <AccordionItem
      value={menu.panel ?? menu.name.toLowerCase()}
      className="border-none"
    >
      <AccordionTrigger
        onClick={() => {
          // ⚠️ 종전엔 `current` 일 때만 닫았고 `router.push` 뒤에는 닫는 코드가
          // **없었다** — 이동해도 시트가 열린 채 남는다(#5386). 새 탭(`_blank`)은
          // 이 창이 그대로 남아야 맞으므로 닫지 않는다.
          if (menu.target === "_blank") {
            window.open(menu.href, "_blank", "noopener,noreferrer");
            return;
          }
          if (!link.current) {
            router.push(menu.href, undefined, { locale: languageCode });
          }
          link.onClick({ preventDefault: () => {} });
        }}
        size="lg"
        className={ROW}
      >
        <span style={{ color: menu.color }}>{t(menu.name)}</span>
      </AccordionTrigger>
    </AccordionItem>
  );
}

/**
 * `store` 패널 전용 — 시안 `panel/Store`(`29303:21604`).
 *
 * ★**데스크톱과 같은 3그룹이다.** 종전엔 「Explore 링크 3개 + 평평한 제품 목록」이었다:
 *
 *   group-label 햅틱 수트 …      row × 2   (Pro · Air)
 *   group-label 확장 기어 …      row × N   (Sleeve · Glove · Visor …)
 *   group-label 전체 라인업 …    link × 3  (번들 · 액세서리 · 전체 제품 보기 →)
 *
 * 문구·순서·그룹 정의는 `_const/store-panel.ts` 한 곳에서 읽는다 — 데스크톱
 * (`navigation-menu-content.tsx`)과 같은 출처다. 화면별로 다른 건 **치수뿐**이다
 * (썸네일 56 vs 72 · 좌인셋 36 vs 12).
 */
function TactSuitMenu({ menu }: { menu: Menu & { panel: PanelKey } }) {
  const { t } = useTrans(["gnb"]);
  const { data: products } = useProducts();
  const priced = usePricedFilter();
  const { torso, extras } = splitStoreGroups(priced(products));
  // 훅은 여기서 **한 번만** 부른다. `LineupLink` 안에서 부르면 링크 3개가 각자
  // 구독하는데 배지가 붙는 건 한 행뿐이다.
  const bundleBadges = useBundleSetBadges();

  return (
    <AccordionItem value={menu.panel} className="border-none">
      <AccordionTrigger size="lg" className={ROW}>
        {/* ⚠️ 라벨을 리터럴로 박지 말 것. 종전엔 `{"TactSuit"}` 라서 (a) 7 로케일 어디에서도
            번역되지 않았고 (b) v2 재편으로 이름이 `Store` 가 됐는데도 모바일만 옛 이름을
            노출했다. 데스크탑은 `t(menu.name)` 이라 두 화면이 갈렸다. */}
        <span className="relative">{t(menu.name)}</span>
        <ChevronDownIcon />
      </AccordionTrigger>
      <AccordionContent className="px-0 pb-2 pt-0">
        {/* ★그룹 라벨 3개를 걷었다 (#5605) — 모바일 시안이 셋 다 `hidden="true"` 다
            (`29303:21605`·`21621`·`21649`). **데스크탑은 그대로 보인다**
            (`29229:21384`·`21401`·`21433`) — 두 화면이 갈라진 자리라 `STORE_GROUP_LABELS`
            는 남는다(데스크탑이 쓴다).

            라벨이 없어도 **순서는 그룹 순서 그대로**다 — `splitStoreGroups` 가 정하는
            torso → extras 를 이어 붙인다. 평평해졌다고 정렬을 바꾸면 시안과 어긋난다. */}
        {[...torso, ...extras].map((product) => (
          <StoreRow key={product.id} product={product} />
        ))}

        {STORE_LINEUP_LINKS.map((item) => (
          <LineupLink
            key={item.href}
            item={item}
            badges={item.href === BADGED_LINEUP_HREF ? bundleBadges : undefined}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}

/**
 * 탭 링크를 **컴포넌트로 뺀다** — `map` 안에서 `useMenuLink` 를 부르면 훅 규칙 위반이다
 * (`react-hooks/rules-of-hooks`). 이 파일에 같은 형태가 둘 있었고 하나는 죽은 코드와 함께
 * 사라졌다. 항목 수가 바뀌면 훅 호출 수가 바뀌는 구조라 실제로 깨질 수 있는 자리다.
 */
function GamesTabLink({
  tab,
  index,
}: {
  tab: { label: string; href: string };
  index: number;
}) {
  const { t } = useTrans(["gnb"]);
  const link = useMenuLink(tab.href);
  return (
    <Link
      href={tab.href}
      onClick={link.onClick}
      className={cn(PANEL_LINK, index === 0 && PANEL_LINK_FIRST)}
    >
      {t(tab.label)}
    </Link>
  );
}

/**
 * 시안 제품 행(`29303:21623`) — `pl-36 pr-16 py-10 gap-12` · 썸네일 **56** r12 ·
 * copy `gap-4`. 데스크톱(72 · `gap-14` · `px-12`)과 치수만 다르다.
 */
function StoreRow({ product }: { product: Product }) {
  const router = useRouter();
  const { t } = useTrans(["gnb"]);
  // ★camo 는 URL 이 베이스 제품의 `?color=camo` 로 정규화돼 경로만으로 못 가른다.
  const href = getProductDetailHref(product.slug as ProductSlug);
  const link = useMenuLink(
    href,
    isProductDetailActive(router.asPath, product.slug),
  );
  const copy = STORE_COPY[product.slug];
  /**
   * ★**시안 우선.** 시안이 그린 5종은 리포가 소유한 이미지를 쓰고, 시안에 없는 제품
   * (TactSuit Air: Camo 등)만 API 값으로 떨어진다 — 실측에서 API 썸네일이 시안과
   * 2장 달랐다(Air 색상 · Glove 한 짝/두 짝). 근거는 `_const/store-panel.ts` 주석.
   */
  const thumb = copy?.thumb ?? product.thumbnail;

  return (
    <Link
      href={href}
      onClick={link.onClick}
      className="flex items-center gap-3 py-2.5 pl-9 pr-4"
    >
      <div className="relative size-14 shrink-0 overflow-hidden rounded-xl">
        {thumb && (
          <Image
            src={thumb}
            alt=""
            fill
            className="object-contain"
            sizes="56px"
          />
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-base font-600 leading-[18px] tracking-[0.25px] text-coolgray-90",
              link.current && "underline",
            )}
          >
            {product.name}
          </span>
          {/* ★세일 중인 제품에 `Sale` (#5603). 시안 제품 행에는 배지가 없지만 그건
              **그날 5종에 `sale` 이 없었기 때문**이고, 배지는 라이브 커머스 신호다
              (`product.tags`). 데스크탑도 같은 술어를 쓴다 — 한쪽만 넣으면 갈라진다.
              prod 실측(2026-09-08): 이 목록에서 `sale` 은 `tactvisor` 한 건. */}
          <MenuItemBadge badge={storeRowBadge(product)} size="sm" />
        </div>
        {copy && (
          /* 데스크탑과 같은 이유로 `highlight`·`tags` 를 걷었다 — 시안이 둘을
             `hidden="true"` 로 껐다. 데이터가 `store-panel.ts` 한 곳이라 두 화면이
             함께 정리된다. */
          <span className="text-sm leading-4 tracking-[0.4px] text-coolgray-50">
            {/* ★모바일 전용 짧은 문구 (#5605). 없으면 데스크탑 문구로 떨어진다 —
                TactVisor 는 두 화면이 같아 `descMobile` 이 없다. */}
            {t(copy.descMobile ?? copy.desc)}
          </span>
        )}
      </div>
    </Link>
  );
}

/** 시안 `link-*`(`29303:21651`) — 제품 행과 같은 들여쓰기, 마지막만 12/16 `blue-60`. */
function LineupLink({
  item,
  badges,
}: {
  item: (typeof STORE_LINEUP_LINKS)[number];
  /** `Bundle Set` 행만 받는다 — 시안이 그 한 행에만 `BadgeArea` 를 뒀다. */
  badges?: BadgeType[];
}) {
  const { t } = useTrans(["gnb"]);
  const link = useMenuLink(item.href);
  return (
    <Link
      href={item.href}
      onClick={link.onClick}
      className={cn(
        PANEL_LINK,
        // 시안 `29303:21651` — 라벨과 배지가 `gap-8`(=gap-2) 로 나란하다. `PANEL_LINK` 는
        // `block` 이라 여기서 `flex` 로 덮는다(`cn` 이 display 충돌을 뒤쪽으로 해소).
        "flex items-center gap-2",
        "cta" in item && item.cta
          ? "text-sm font-600 leading-4 tracking-[0.4px] text-blue-60"
          : link.current && "font-600 text-coolgray-90",
      )}
    >
      {/* 라벨과 `→` 를 한 요소로 묶는다. 풀어두면 flex 아이템이 둘이 되어 `gap-2` 가
          `See all products` 와 화살표 사이에 8px 을 넣는다(종전엔 `block` 이라 안 났다). */}
      <span>
        {t(item.title)}
        {"cta" in item && item.cta ? " →" : null}
      </span>
      {!!badges?.length && (
        <span className="flex items-center gap-1">
          {badges.map((badge) => (
            <MenuItemBadge
              key={badge}
              badge={badge}
              size="sm"
              // 시안 배지 타이포는 `+0.1px` 다. `size="sm"` 은 자간을 안 정해서
              // 링크의 `tracking-[0.25px]` 를 상속했고, 실측에서 모바일 `Sale` 폭이
              // **38 vs 시안 37** 로 1px 어긋났다.
              className="tracking-[0.1px]"
            />
          ))}
        </span>
      )}
    </Link>
  );
}

/** `pc` / `vr` 패널 전용. `panel` 이 아코디언 항목 식별자라 optional 이면 안 된다. */
function GamesMenu({ menu }: { menu: Menu & { panel: PanelKey } }) {
  const { onClose } = useMenuViewContext();
  const { t } = useTrans(["gnb"]);
  /**
   * ★**시안의 sub-nav 로 교체했다** (#5415 · 모바일 `29212:36190`).
   *
   * 종전엔 카테고리 그룹(모든 게임 · 메타 퀘스트 · 스팀 VR …)이었다. 모바일 시안도
   * 데스크톱과 **같은 3항목**이다 — `Overview` · `지원 게임 전체` · `Software`.
   *
   * ⚠️ **데스크톱(`navigation-menu-content.tsx`)과 함께 고쳐야 한다.** 두 파일이 별개
   * 하드코딩이라 한쪽만 바꾸면 갈라진다 — 그 경고가 이 리포에 이미 적혀 있었고
   * `__tests__/v2-components/gnb-support-menu.test.tsx` 가 그 갈라짐을 잠근다.
   *
   * 탭 정의는 `getSubNavTabs` 단일 출처를 쓴다. ★**라벨 덮어쓰기는 걷었다**
   * (2026-09-07 요청) — 종전엔 가운데만 `All supported games` 로 갈랐는데 이제
   * 서브내비와 **같은 문구**(`Supported PC Games` / `Supported VR Games`)다.
   * 데스크톱(`navigation-menu-content.tsx`)도 같이 걷었다.
   */
  const tabs = getSubNavTabs(menu.panel === "vr" ? "/games/vr" : "/games/pc");

  return (
    <AccordionItem value={menu.panel} className="border-none">
      <AccordionTrigger size="lg" className={ROW}>
        <span className="relative">
          {t(menu.name)}
          <Dot visible={menu.dotted} />
        </span>
        <ChevronDownIcon />
      </AccordionTrigger>
      <AccordionContent className="px-0 pb-2 pt-0">
        <ul className="flex flex-col">
          {tabs.map((tab, i) => (
            <GamesTabLink key={tab.href} tab={tab} index={i} />
          ))}
        </ul>
        {/* ★모바일 시안에도 **인기 타이틀 레일이 있다**(`29304:21613` PC ·
            `29304:241268` VR) — 카드 3장 96×144. 데스크톱에만 붙어 있었다. */}
        <PopularTitlesRail
          platform={menu.panel === "vr" ? "vr" : "pc"}
          variant="mobile"
          onClick={() => onClose?.()}
        />
      </AccordionContent>
    </AccordionItem>
  );
}

/** `index` 는 시안의 「첫 항목만 SemiBold」 위계 표현에 쓴다. */
function SubMenuItem({ subMenu, index }: { subMenu: SubMenu; index: number }) {
  const link = useMenuLink(subMenu.href);
  const { t } = useTrans(["gnb"]);

  return (
    <li key={subMenu.name} className={cn("group flex items-center")}>
      <Link
        href={subMenu.href}
        target={subMenu.target}
        className={cn(PANEL_LINK, "w-full", index === 0 && PANEL_LINK_FIRST)}
        onClick={link.onClick}
      >
        <span className="relative inline-flex items-center gap-2">
          <span className="relative">
            {t(subMenu.name)}
            <Dot visible={subMenu.dotted} />
          </span>
          {/* 종전 기본값(`className="text-xs"` · size 미지정=`md`)을 그대로 명시로
              옮긴 것이다. 「전체 라인업」의 `Bundle Set` 배지는 시안이 `sm` 이라
              치수가 다르다 — `use-bundle-set-badges.ts` 참고. */}
          <MenuItemBadge badge={subMenu.badge} className="text-xs" />
        </span>
      </Link>
    </li>
  );
}

function LocaleButton({ ...rest }: React.ComponentProps<"button">) {
  const { getLanguageName, locale, countryCode, languageCode } = useLocale();
  const { t } = useTrans(["gnb"]);

  return (
    <button
      className="flex h-14 shrink-0 items-center justify-between bg-white px-4"
      {...rest}
    >
      {locale === undefined ? (
        <div className="flex items-center gap-2">
          <GlobeIcon className="size-6" />
          <p className="text-base text-coolgray-90">
            {t("Shipping & Language")}
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex aspect-square items-center justify-center overflow-hidden rounded-full border border-black">
            <CountryFlag
              countryCode={countryCode}
              className="flex aspect-square scale-x-[1.3]"
            />
          </div>

          <p className="text-base font-600 text-coolgray-90">
            {[countryCode, getLanguageName()].join(" · ")}
          </p>
        </div>
      )}
      <ChevronRightIcon className="size-6" />
    </button>
  );
}
